// @flow
import * as React from 'react';
import { Trash } from 'grommet-icons';
import { FormattedMessage } from 'react-intl';
import { ApiClient } from 'speed-js-core';
import { connectAPI, withRouter } from 'speed-js-react';
import { withTable } from 'speed-js-react-web';
import { get } from 'lodash';
import { Button } from 'grommet';
import MessageDialog from 'esm-web-pro/components/dialogs/MessageDialog';

type Props = {
    // eslint-disable-next-line
    resourceName: string,
    // eslint-disable-next-line
    resourceArguments: Array<any>,
    // eslint-disable-next-line
    label: string,
    accessor: string,
    row: Object,
    deleteObject: Function,
    table: {
        reloadData: () => void,
    },
    nameAccessor: string,
    icon?: React.Node,
    // eslint-disable-next-line
    resourceMethod: string,
    deactivate?: boolean,
    reload?: () => void,
}


type State = {
    open: boolean,
    errorMessage: React.Node | null,
}

@withRouter
@withTable
@connectAPI((apiClient: ApiClient, props: Props) => ({
    deleteObject: {
        send: (id: any) => {
            const resource = get(apiClient.resources, props.resourceName);
            const resourceArguments = props.resourceArguments || [];
            const resourceMethod = props.resourceMethod || null;
            if (resourceMethod) {
                return resource[resourceMethod](...resourceArguments, id);
            }
            return resource.delete(...resourceArguments, id);
        },
    },
}))
class DeleteComponent extends React.Component<Props, State> {
    static defaultProps: Object;

    state = {
        open: false,
        errorMessage: null,
    };

    handleOpen = () => {
        this.setState({ open: true });
    };

    handleConfirm = (e: SyntheticMouseEvent<HTMLButtonElement>) => {
        const {
            accessor, row, deleteObject, table, nameAccessor, deactivate, reload,
        } = this.props;
        const id = row._original[accessor];

        deleteObject(
            id,
        ).then((response) => {
            if (response instanceof Error) {
                this.setState({
                    errorMessage: deactivate ? (
                        <FormattedMessage
                            id="confirmation.deactivate-error"
                            defaultMessage="Could not deactivate {object}. Please try again later?"
                            values={{
                                object: get(
                                    row,
                                    nameAccessor,
                                    <FormattedMessage id="this-object" defaultMessage="this object" />,
                                ),
                            }}
                        />
                    ) : (
                        <FormattedMessage
                            id="confirmation.delete-error"
                            defaultMessage="Could not delete {object}. Please try again later?"
                            values={{
                                object: get(
                                    row,
                                    nameAccessor,
                                    <FormattedMessage id="this-object" defaultMessage="this object" />,
                                ),
                            }}
                        />
                    ),
                    open: false,
                });
            } else if (response.status === 400 && response.data.detail) {
                this.setState({
                    errorMessage: response.data.detail,
                    open: false,
                });
            } else {
                this.setState({ open: false }, () => {
                    table.reloadData();
                    if (reload) {
                        reload();
                    }
                });
            }
        });
    };

    render() {
        const { open, errorMessage } = this.state;
        const {
            row, nameAccessor, icon, deactivate,
        } = this.props;
        let message = null;

        if (open) {
            message = (
                <MessageDialog
                    message={deactivate ? (
                        <FormattedMessage
                            id="confirmation.deactivate"
                            defaultMessage="Are you sure you want to deactivate {object}?"
                            values={{
                                object: get(
                                    row,
                                    nameAccessor,
                                    <FormattedMessage id="this-object" defaultMessage="this object" />,
                                ),
                            }}
                        />
                    ) : (
                        (
                            <FormattedMessage
                                id="confirmation.delete"
                                defaultMessage="Are you sure you want to delete {object}?"
                                values={{
                                    object: get(
                                        row,
                                        nameAccessor,
                                        <FormattedMessage id="this-object" defaultMessage="this object" />,
                                    ),
                                }}
                            />
                        )
                    )}
                    actions={{
                        no: {
                            label: (
                                <FormattedMessage
                                    id="cancel-btn"
                                    defaultMessage="Cancel"
                                />
                            ),
                            type: 'negative',
                            callback: (e: SyntheticMouseEvent<HTMLButtonElement>) => this.setState({ open: false }),
                        },
                        yes: {
                            label: (
                                <FormattedMessage
                                    id="confirm-btn"
                                    defaultMessage="Confirm"
                                />
                            ),
                            callback: this.handleConfirm,
                            type: 'positive',
                        },
                    }}
                />
            );
        } else if (errorMessage) {
            message = (
                <MessageDialog
                    message={errorMessage}
                    actions={{
                        ok: {
                            label: (
                                <FormattedMessage
                                    id="ok"
                                    defaultMessage="Ok"
                                />
                            ),
                            callback: (e: SyntheticMouseEvent<HTMLButtonElement>) => this.setState({
                                errorMessage: false,
                            }),
                        },
                    }}
                />
            );
        }

        return (
            <React.Fragment>
                <Button
                    icon={icon}
                    position="right"
                    color="status-error"
                    plain={false}
                    onClick={this.handleOpen}
                />
                {message}
            </React.Fragment>
        );
    }
}

DeleteComponent.defaultProps = {
    nameAccessor: 'name',
    icon: <Trash />,
    deactivate: false,
    reload: () => {},
};

export default DeleteComponent;
